import { traduzirErroSupabase } from '@octawash/shared';
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase, tenantCodeAtual } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant, carregando: tenantCarregando } = useTenant();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);

  const destino = (location.state as { from?: Location })?.from?.pathname ?? '/dashboard';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password: senha });
    if (error) {
      setEnviando(false);
      toast.error('Falha no login', { description: traduzirErroSupabase(error) });
      return;
    }

    // Bootstrap claim de tenant (idempotente; AuthProvider também chama no SIGNED_IN)
    try {
      await supabase.functions.invoke('bootstrap-tenant-claim', {
        body: { cod_tenant: tenantCodeAtual, hostname: window.location.hostname },
      });
      await supabase.auth.refreshSession();
    } catch {
      // ignora — AuthProvider tenta de novo
    }

    setEnviando(false);
    toast.success('Bem-vindo de volta!');
    navigate(destino, { replace: true });
  }

  async function handleGoogleLogin() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + '/dashboard' },
    });
    if (error) toast.error('Falha com Google', { description: traduzirErroSupabase(error) });
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-5">
      {/* Lateral hero — gradient OctaWash com mesh decorativo */}
      <div className="bg-octa-gradient relative hidden flex-col justify-between overflow-hidden p-12 text-white lg:col-span-2 lg:flex">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 -top-20 h-96 w-96 rounded-full bg-white/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-white/5 blur-3xl"
        />
        <div className="relative z-10 flex items-center gap-3 text-xl font-semibold">
          {tenant?.des_logo_url ? (
            <img
              src={tenant.des_logo_url}
              alt={tenant.nom_tenant}
              className="h-12 w-12 rounded-2xl bg-white/10 object-cover backdrop-blur-sm"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 font-bold backdrop-blur-sm">
              {(tenant?.nom_tenant ?? 'OW').slice(0, 2).toUpperCase()}
            </div>
          )}
          <span>{tenant?.nom_tenant ?? 'Octa Pontos'}</span>
        </div>
        <div className="relative z-10 space-y-4">
          <p className="text-4xl font-bold leading-tight tracking-tight">
            Gestao completa do
            <br />
            seu lava-jato.
          </p>
          <p className="text-lg text-white/80">
            Assinaturas, frota, lavagens em tempo real, relatorios, fidelidade e LGPD em um so
            lugar.
          </p>
        </div>
        <p className="relative z-10 text-xs text-white/60">
          (c) {new Date().getFullYear()} Octa Pontos
        </p>
      </div>

      {/* Form lateral direito */}
      <div className="bg-octa-hero relative flex flex-col items-center justify-center p-6 sm:p-12 lg:col-span-3">
        <div
          aria-hidden
          className="bg-octa-accent pointer-events-none absolute right-12 top-12 h-32 w-32 rounded-full opacity-20 blur-2xl"
        />
        <div className="glass shadow-octa-lg relative z-10 w-full max-w-sm space-y-6 rounded-2xl p-8">
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold">Entrar no painel</h1>
            <p className="text-muted-foreground text-sm">
              {tenantCarregando
                ? 'Carregando configurações...'
                : tenant
                  ? `Acesse a gestão do ${tenant.nom_tenant}.`
                  : 'Use sua conta de administrador.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@empresa.com.br"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="senha">Senha</Label>
              </div>
              <Input
                id="senha"
                type="password"
                required
                autoComplete="current-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
              />
            </div>
            <Button type="submit" className="w-full" disabled={enviando}>
              {enviando ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background text-muted-foreground px-2">ou</span>
            </div>
          </div>

          <Button variant="outline" className="w-full" onClick={handleGoogleLogin}>
            Continuar com Google
          </Button>

          <p className="text-muted-foreground text-center text-xs">
            Nao tem conta?{' '}
            <a href="/cadastro" className="text-primary hover:underline">
              Criar conta
            </a>
          </p>
          <p className="text-muted-foreground text-center text-xs">
            Tem um lava-jato?{' '}
            <a href="/onboarding" className="text-primary font-medium hover:underline">
              Cadastre seu negocio aqui
            </a>
          </p>
          <p className="text-muted-foreground text-center text-xs">
            Esqueceu a senha? Fale com o suporte:{' '}
            <a
              href={`mailto:${tenant?.des_email_suporte ?? 'suporte@octapontos.com.br'}`}
              className="text-primary hover:underline"
            >
              {tenant?.des_email_suporte ?? 'suporte@octapontos.com.br'}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
